import { CircleAlert, CircleCheck } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { colors, ds } from '@/config/colors';

import { WelcomeSubtitle, WelcomeTitle } from '../auth.styled';
import { AuthLayout } from '../components/AuthLayout';

type StaffResetStatusVariant = 'success' | 'linkError' | 'accountDisabled';

interface StaffResetStatusProps {
  variant: StaffResetStatusVariant;
}

/* Układ potwierdzenia jak w staff-preview (15-reset-hasla, stan rp-sent): ikona w kole 56 px, tytuł i tekst wyśrodkowane. */
const Status = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0 8px;
`;

const IconCircle = styled.div<{ $variant: StaffResetStatusVariant }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  background: ${({ $variant, theme }) => {
    if ($variant === 'success') return theme.mode === 'dark' ? colors.success[900] : ds.bgMint1;
    return theme.mode === 'dark' ? colors.danger[900] : colors.danger[50];
  }};
  color: ${({ $variant, theme }) => {
    if ($variant === 'success') return theme.mode === 'dark' ? colors.success[300] : ds.textPrimary;
    return theme.mode === 'dark' ? colors.danger[300] : colors.danger[600];
  }};

  svg {
    width: 26px;
    height: 26px;
  }
`;

const StatusText = styled(WelcomeSubtitle)`
  max-width: 340px;
  margin-bottom: 0;
  font-size: 15px;
`;

/* Ekrany końcowe wariantu Staff: bez przekierowania i bez linków do panelu, bo pracownik loguje się w aplikacji. */
export const StaffResetStatus: FC<StaffResetStatusProps> = ({ variant }) => {
  const { t } = useTranslation();
  const isSuccess = variant === 'success';

  const textsByVariant: Record<StaffResetStatusVariant, { title: string; description: string }> = {
    success: {
      title: t('auth.resetPassword.staff.successTitle', 'Hasło zmienione'),
      description: t(
        'auth.resetPassword.staff.successDescription',
        'Wróć do aplikacji HotelSpot Staff i zaloguj się nowym hasłem.',
      ),
    },
    linkError: {
      title: t('auth.resetPassword.staff.linkErrorTitle', 'Link wygasł lub jest nieprawidłowy'),
      description: t(
        'auth.resetPassword.staff.linkErrorDescription',
        'Poproś o nowy link w aplikacji HotelSpot Staff: na ekranie logowania wybierz „Zapomniałeś hasła?”.',
      ),
    },
    accountDisabled: {
      title: t('auth.resetPassword.staff.accountDisabledTitle', 'Konto jest wyłączone'),
      description: t(
        'auth.resetPassword.staff.accountDisabledDescription',
        'Nie możesz teraz ustawić hasła. Skontaktuj się z administratorem hotelu, aby włączył Twoje konto.',
      ),
    },
  };
  const texts = textsByVariant[variant];

  return (
    <AuthLayout brand="staff">
      <Status role={isSuccess ? 'status' : 'alert'}>
        <IconCircle $variant={variant} aria-hidden="true">
          {isSuccess ? <CircleCheck /> : <CircleAlert />}
        </IconCircle>
        <WelcomeTitle>{texts.title}</WelcomeTitle>
        <StatusText>{texts.description}</StatusText>
      </Status>
    </AuthLayout>
  );
};
